import { useCallback, useMemo, useState } from "react";

import { styled } from "styled-components";

import { useTranslate } from "@similarweb/i18n-provider";
import { Table } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";

import { getComparisonColumns } from "./SegmentsComparisonTableColumns";
import type { ComparisonRow, RowActionRenderer } from "./types";

const ComparisonTableWrapper = styled.div`
    .ant-table-container {
        border-top: 1px solid ${colorsPalettes.carbon["100"]};
    }

    .ant-table-tbody .ant-table-row {
        cursor: pointer;
    }

    .ant-table-tbody .comparison-row-actions {
        display: flex;
        align-items: center;
        gap: 4px;
        opacity: 0;
        pointer-events: none;
        transition: opacity 120ms ease-in-out;
    }

    .ant-table-tbody .ant-table-row:hover .comparison-row-actions {
        opacity: 1;
        pointer-events: auto;
    }
`;

const GROUPS_PER_PAGE = 5;

interface ISegmentsComparisonTableProps {
    data: ComparisonRow[];
    isLoading: boolean;
    onRowClick: (row: ComparisonRow) => void;
    rowActionRenderers: RowActionRenderer[];
    noData: {
        title: string;
        subtitle: string;
    };
}

const filterByQuery = (rows: ComparisonRow[], query: string): ComparisonRow[] => {
    if (!query) {
        return rows;
    }
    const normalized = query.toLowerCase();
    return rows.filter((row) => row.name?.toLowerCase().includes(normalized));
};

export const SegmentsComparisonTable = ({
    data,
    isLoading,
    onRowClick,
    rowActionRenderers,
    noData,
}: ISegmentsComparisonTableProps) => {
    const { translate } = useTranslate();
    const [searchQuery, setSearchQuery] = useState("");

    const filteredData = useMemo(() => filterByQuery(data, searchQuery), [data, searchQuery]);

    const columns = useMemo(
        () =>
            getComparisonColumns({
                rowActionRenderers,
                translate,
            }),
        [rowActionRenderers, translate],
    );

    const handleSearchChange = useCallback((query: string) => {
        setSearchQuery(query);
    }, []);

    const hasSearchQuery = searchQuery.trim().length > 0;
    const noSearchResults = hasSearchQuery && filteredData.length === 0;

    const noDataConfig = noSearchResults
        ? {
              title: translate("common.noresults"),
              subtitle: translate("common.noresults.subtitle"),
              showHeaderWhenEmpty: true,
          }
        : { title: noData.title, subtitle: noData.subtitle, showHeaderWhenEmpty: true };

    return (
        <ComparisonTableWrapper data-automation="segments-new-comparison-table">
            <Table<ComparisonRow>
                rowKey="id"
                data={filteredData}
                columnConfig={columns}
                isLoading={isLoading}
                showHeader={false}
                withArrowButtons={false}
                theme="gray"
                pagination={{
                    total: filteredData.length,
                    pageSize: GROUPS_PER_PAGE,
                }}
                noData={noDataConfig}
                onRow={(row: ComparisonRow) => ({
                    onClick: () => onRowClick(row),
                })}
            >
                <Table.TableTop>
                    <Table.TableTop.Filters
                        filters={[
                            {
                                filter: "search",
                                placeholder: translate("common.search.placeholder"),
                                onSearchChange: handleSearchChange,
                                initialValue: searchQuery,
                            },
                        ]}
                    />
                </Table.TableTop>
            </Table>
        </ComparisonTableWrapper>
    );
};
